import React, { useState, useEffect } from 'react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Switch } from '@/shared/components/ui/Switch';
import { Badge } from '@/shared/components/ui/badge';
import { UploadInput } from '@/shared/components/ui/UploadInput';
import type { ChannelResponse, HostedPageConfig, HostedPageLayout, WidgetFeatures } from '@troia-v3/types';
import { useWidgetEmbedScript, useWidgetConfig, useUpdateWidgetConfig } from '../hooks';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
/**
 * Drawer for Widget Settings
 * Shows embed script, customization options, domain whitelist, and hosted page config
 *
 * @example
 * <WidgetSettingsModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   channel={channel}
 * />
 */
export interface WidgetSettingsModalProps {
  /** Drawer open state */
  isOpen: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Channel data */
  channel: ChannelResponse;
}

type TabKey = 'embed' | 'customize' | 'domains' | 'hosted-page' | 'features';

const tabs: Array<{ key: TabKey; label: string; icon: IconName }> = [
  { key: 'embed', label: 'Código Embed', icon: 'code' },
  { key: 'customize', label: 'Customização', icon: 'eye' },
  { key: 'domains', label: 'Domínios', icon: 'globe' },
  { key: 'hosted-page', label: 'Página Pública', icon: 'link' },
  { key: 'features', label: 'Funcionalidades', icon: 'sliders' },
];

// ---------------------------------------------------------------------------
// Mini layout preview for hosted page tab
// ---------------------------------------------------------------------------
function LayoutPreview({ layout, brandColor }: { layout: HostedPageLayout; brandColor: string }) {
  if (layout === 'centered') {
    return (
      <div className="h-12 rounded flex items-center justify-center" style={{ background: brandColor }}>
        <div className="h-9 w-3/5 bg-white rounded-sm" />
      </div>
    );
  }
  if (layout === 'fullbleed') {
    return <div className="h-12 rounded" style={{ background: brandColor }} />;
  }
  // split
  return (
    <div className="h-12 flex rounded overflow-hidden">
      <div className="flex-1" style={{ background: brandColor }} />
      <div className="flex-1 bg-white border border-gray-200" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const WidgetSettingsModal: React.FC<WidgetSettingsModalProps> = ({
  isOpen,
  onClose,
  channel,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('embed');
  const [copied, setCopied] = useState(false);
  const [copiedHostedUrl, setCopiedHostedUrl] = useState(false);

  // channel.config é Record<string, unknown>; extraímos os campos do widget
  // com casts específicos para cada tipo esperado.
  const widgetConfig = channel.config as {
    brandColor?: string;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    welcomeMessage?: string;
    agentName?: string;
    suggestions?: Array<{ icon: string; text: string }>;
    hostedPage?: HostedPageConfig;
    features?: WidgetFeatures;
  } | undefined;

  // Configuration state
  const [brandColor, setBrandColor] = useState<string>(widgetConfig?.brandColor || '#3B82F6');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'>(
    widgetConfig?.position || 'bottom-right'
  );
  const [welcomeMessage, setWelcomeMessage] = useState<string>(widgetConfig?.welcomeMessage || 'Olá! Como posso ajudar?');
  const [agentName, setAgentName] = useState<string>(widgetConfig?.agentName || 'Atendimento');
  const [suggestions, setSuggestions] = useState<Array<{ icon: string; text: string }>>(
    widgetConfig?.suggestions || []
  );

  // Hosted page state
  const [hostedPage, setHostedPage] = useState<HostedPageConfig>(
    widgetConfig?.hostedPage ?? { enabled: false, layout: 'centered' }
  );

  // Features state
  const [features, setFeatures] = useState<WidgetFeatures>(
    widgetConfig?.features ?? {}
  );

  // Fetch embed script (condicionado ao isOpen — CLAUDE.md NUNCA #14)
  const { data: embedData, isLoading: loadingEmbed } = useWidgetEmbedScript(isOpen ? channel.id : undefined);

  // Fetch saved widget config from API
  const { data: widgetConfigData } = useWidgetConfig(isOpen ? channel.id : undefined);

  // Update mutation
  const updateConfig = useUpdateWidgetConfig();

  // Populate form state from API data (fresh) or channel prop (fallback)
  useEffect(() => {
    const config = (widgetConfigData?.config || channel.config) as {
      brandColor?: string;
      position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
      welcomeMessage?: string;
      agentName?: string;
      suggestions?: Array<{ icon: string; text: string }>;
      hostedPage?: HostedPageConfig;
      features?: WidgetFeatures;
    } | undefined;
    if (config) {
      setBrandColor(config.brandColor || '#3B82F6');
      setPosition(config.position || 'bottom-right');
      setWelcomeMessage(config.welcomeMessage || 'Olá! Como posso ajudar?');
      setAgentName(config.agentName || 'Atendimento');
      setSuggestions(config.suggestions || []);
      if (config.hostedPage) {
        setHostedPage(config.hostedPage);
      }
      if (config.features) {
        setFeatures(config.features);
      }
    }
  }, [widgetConfigData, channel]);

  const handleCopyScript = () => {
    if (embedData?.embedScript) {
      navigator.clipboard.writeText(embedData.embedScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyHostedUrl = () => {
    if (embedData?.hostedPageUrl) {
      navigator.clipboard.writeText(embedData.hostedPageUrl);
      setCopiedHostedUrl(true);
      setTimeout(() => setCopiedHostedUrl(false), 2000);
    }
  };

  const updateHostedPage = (patch: Partial<HostedPageConfig>) => {
    setHostedPage((prev) => ({ ...prev, ...patch }));
  };

  const handleSaveConfig = async () => {
    await updateConfig.mutateAsync({
      channelId: channel.id,
      config: {
        brandColor,
        position,
        welcomeMessage,
        agentName,
        suggestions,
        hostedPage,
        features,
      },
    });
    // TODO: Show success toast
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-2 w-full">
      <Button
        onClick={onClose}
        variant="secondary"
        data-testid="widget-settings-close"
      >
        Fechar
      </Button>
      <Button
        onClick={handleSaveConfig}
        disabled={updateConfig.isPending}
        data-testid="widget-settings-save"
      >
        <Icon name="save" size={16} className="mr-2" />
        {updateConfig.isPending ? 'Salvando...' : 'Salvar alterações'}
      </Button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      icon={<Icon name="code" className="w-5 h-5" />}
      title="Configurações do Widget"
      tabs={{
        items: tabs.map((tab) => ({ id: tab.key, label: tab.label, icon: tab.icon })),
        active: activeTab,
        onChange: (tabId) => setActiveTab(tabId as TabKey),
      }}
      footer={footerContent}
    >
      <div className="px-6 py-5">
      {/* Tab 1: Embed Script */}
      {activeTab === 'embed' && (
        <div className="space-y-4">
          <div
            className="rounded-lg p-4 border-l-4"
            style={{ backgroundColor: '#eff6ff', borderColor: '#3b82f6' }}
          >
            <div className="flex items-start gap-3">
              <Icon name="code" size={20} style={{ color: '#3b82f6' }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold mb-1" style={{ color: '#1e3a8a' }}>
                  Como usar?
                </h4>
                <p className="text-xs" style={{ color: '#1e40af' }}>
                  Copie o código abaixo e cole antes do fechamento da tag &lt;/body&gt; do seu site.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Código de Incorporação
            </label>
            <div className="relative">
              {loadingEmbed ? (
                <div
                  className="w-full h-40 rounded-lg border flex items-center justify-center"
                  style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
                >
                  <p className="text-sm" style={{ color: '#64748b' }}>
                    Carregando código...
                  </p>
                </div>
              ) : (
                <>
                  <Textarea
                    value={embedData?.embedScript || ''}
                    readOnly
                    rows={11}
                    className="text-xs font-mono"
                    resize="none"
                    style={{ backgroundColor: '#f9fafb' }}
                  />
                  <button
                    onClick={handleCopyScript}
                    className="absolute top-2 right-2 px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1"
                    style={{
                      backgroundColor: copied ? '#10b981' : '#8b5cf6',
                      color: '#ffffff',
                    }}
                  >
                    {copied ? (
                      <>
                        <Icon name="check-circle" size={14} />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Icon name="copy" size={14} />
                        Copiar
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div
            className="rounded-lg p-4 border"
            style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
          >
            <h4 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>
              Informações do Widget
            </h4>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span style={{ color: '#64748b' }}>Canal:</span>
                <span style={{ color: '#1e293b' }} className="font-medium">
                  {channel.name}
                </span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: '#64748b' }}>Domínio:</span>
                <span style={{ color: '#1e293b' }} className="font-medium">
                  {channel.identifier}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: '#64748b' }}>Status:</span>
                <Badge variant={channel.status === 'active' ? 'success' : channel.status === 'inactive' ? 'secondary' : 'destructive'}>{channel.status}</Badge>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Customização */}
      {activeTab === 'customize' && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: '#64748b' }}>
            Personalize a aparência do widget conforme sua marca.
          </p>

          {/* Cor Principal */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Cor Principal
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="h-10 w-20 rounded cursor-pointer"
              />
              <Input
                type="text"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="flex-1"
              />
            </div>
          </div>

          {/* Posição */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Posição na Tela
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'bottom-right' as const, label: 'Inferior Direita' },
                { value: 'bottom-left' as const, label: 'Inferior Esquerda' },
                { value: 'top-right' as const, label: 'Superior Direita' },
                { value: 'top-left' as const, label: 'Superior Esquerda' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setPosition(opt.value)}
                  className="px-4 py-2 text-sm rounded-lg border transition-colors"
                  style={{
                    backgroundColor: position === opt.value ? '#eff6ff' : '#ffffff',
                    borderColor: position === opt.value ? '#3b82f6' : '#e5e7eb',
                    color: position === opt.value ? '#1e40af' : '#64748b',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mensagem de Boas-Vindas */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Mensagem de Boas-Vindas
            </label>
            <Textarea
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              rows={3}
              resize="none"
            />
          </div>

          {/* Nome do Agente */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Nome do Agente
            </label>
            <Input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
            />
          </div>

          {/* Sugestões Rápidas */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Sugestões Rápidas
            </label>
            <p className="text-xs mb-3" style={{ color: '#64748b' }}>
              Opções rápidas exibidas ao visitante antes da primeira mensagem.
            </p>
            <div className="space-y-2">
              {suggestions.map((suggestion, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    type="text"
                    value={suggestion.icon}
                    onChange={(e) => {
                      const updated = [...suggestions];
                      updated[index] = { ...updated[index], icon: e.target.value };
                      setSuggestions(updated);
                    }}
                    placeholder="💬"
                    className="w-14 text-center"
                    fullWidth={false}
                    maxLength={2}
                  />
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={suggestion.text}
                      onChange={(e) => {
                        const updated = [...suggestions];
                        updated[index] = { ...updated[index], text: e.target.value };
                        setSuggestions(updated);
                      }}
                      placeholder="Texto da sugestão"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setSuggestions(suggestions.filter((_, i) => i !== index));
                    }}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: '#ef4444' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon name="trash" size={16} />
                  </button>
                </div>
              ))}
            </div>
            {suggestions.length < 5 && (
              <button
                onClick={() => setSuggestions([...suggestions, { icon: '💬', text: '' }])}
                className="mt-2 flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed transition-colors"
                style={{
                  borderColor: '#d1d5db',
                  color: '#64748b',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Icon name="plus" size={14} />
                Adicionar sugestão
              </button>
            )}
          </div>

          {/* Preview */}
          <div
            className="rounded-lg p-4 border"
            style={{ backgroundColor: '#f9fafb', borderColor: '#e5e7eb' }}
          >
            <h4 className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>
              Preview
            </h4>
            <div className="flex items-start gap-3">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                style={{ backgroundColor: brandColor }}
              />
              <div className="space-y-1">
                <p className="text-xs font-medium" style={{ color: '#1e293b' }}>
                  {agentName}
                </p>
                <p className="text-xs" style={{ color: '#64748b' }}>
                  {welcomeMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Domínios */}
      {activeTab === 'domains' && (
        <div className="space-y-4">
          <div
            className="rounded-lg p-4 border-l-4"
            style={{ backgroundColor: '#fef3c7', borderColor: '#f59e0b' }}
          >
            <div className="flex items-start gap-3">
              <Icon name="globe" size={20} style={{ color: '#f59e0b' }} className="flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold mb-1" style={{ color: '#92400e' }}>
                  Segurança
                </h4>
                <p className="text-xs" style={{ color: '#b45309' }}>
                  Apenas domínios listados abaixo podem usar este widget. Isso previne uso não autorizado.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Domínio Principal
            </label>
            <Input
              type="text"
              value={channel.identifier}
              disabled
              className="opacity-60"
            />
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              Configurado durante a criação do canal
            </p>
          </div>

          {/* Future feature placeholder */}
          <div
            className="rounded-lg p-4"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}
          >
            <p className="text-sm" style={{ color: '#64748b' }}>
              <strong>Em breve:</strong> Suporte para múltiplos domínios (subdomínios, ambientes de teste, etc.)
            </p>
          </div>
        </div>
      )}

      {/* Tab 4: Página Pública */}
      {activeTab === 'hosted-page' && (
        <div className="space-y-4">
          {/* Card 1: Toggle habilitar */}
          <div className="rounded-lg border p-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                  Habilitar página pública
                </div>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: '#64748b' }}>
                  Permite que clientes sem site usem uma URL hospedada pelo TROIA com o chat embutido.
                </p>
              </div>
              <div data-testid="hosted-page-toggle">
                <Switch
                  checked={hostedPage.enabled}
                  onChange={(checked) => updateHostedPage({ enabled: checked })}
                />
              </div>
            </div>
          </div>

          {/* Card 2: URL pública (só quando habilitado e URL disponível) */}
          {hostedPage.enabled && embedData?.hostedPageUrl && (
            <div className="rounded-lg border p-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#64748b' }}>
                URL pública
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 truncate rounded-md border px-3 py-2 font-mono text-sm"
                  style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', color: '#374151' }}
                >
                  {embedData.hostedPageUrl}
                </div>
                <button
                  type="button"
                  onClick={handleCopyHostedUrl}
                  className="rounded-md border px-3 py-2 text-xs font-medium transition-colors"
                  data-testid="hosted-page-copy"
                  title="Copiar URL"
                  style={{
                    borderColor: copiedHostedUrl ? '#10b981' : '#d1d5db',
                    backgroundColor: copiedHostedUrl ? '#d1fae5' : '#ffffff',
                    color: copiedHostedUrl ? '#065f46' : '#374151',
                  }}
                >
                  {copiedHostedUrl ? 'Copiado!' : 'Copiar'}
                </button>
                <button
                  type="button"
                  onClick={() => window.open(embedData.hostedPageUrl, '_blank')}
                  className="rounded-md px-3 py-2 text-xs font-medium text-white transition-colors"
                  data-testid="hosted-page-open"
                  title="Abrir em nova aba"
                  style={{ backgroundColor: '#8b5cf6' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8b5cf6'; }}
                >
                  Abrir ↗
                </button>
              </div>
            </div>
          )}

          {/* Aviso quando habilitado mas URL ainda não disponível (config ainda não salva) */}
          {hostedPage.enabled && !embedData?.hostedPageUrl && (
            <div
              className="rounded-lg p-3 border-l-4 text-xs"
              style={{ backgroundColor: '#eff6ff', borderColor: '#3b82f6', color: '#1e40af' }}
            >
              Salve as alterações para gerar a URL pública.
            </div>
          )}

          {/* Card 3: Layout (só quando habilitado) */}
          {hostedPage.enabled && (
            <div className="rounded-lg border p-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
              <div className="text-sm font-semibold mb-3" style={{ color: '#1e293b' }}>
                Layout
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'centered' as const, label: 'Centered' },
                  { value: 'fullbleed' as const, label: 'Fullbleed' },
                  { value: 'split' as const, label: 'Split' },
                ] satisfies Array<{ value: HostedPageLayout; label: string }>).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateHostedPage({ layout: opt.value })}
                    className="rounded-md border p-2 text-left transition-colors"
                    data-testid={`hosted-page-layout-${opt.value}`}
                    style={{
                      borderColor: hostedPage.layout === opt.value ? '#8b5cf6' : '#e5e7eb',
                      backgroundColor: hostedPage.layout === opt.value ? '#f5f3ff' : '#ffffff',
                    }}
                  >
                    <LayoutPreview layout={opt.value} brandColor={brandColor} />
                    <div
                      className="mt-2 text-xs font-semibold text-center"
                      style={{ color: hostedPage.layout === opt.value ? '#7c3aed' : '#374151' }}
                    >
                      {opt.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Card 4: Customização condicional ao layout */}
          {hostedPage.enabled && hostedPage.layout !== 'fullbleed' && (
            <div className="rounded-lg border p-4 space-y-3" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
              <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                Customização
              </div>

              {hostedPage.layout === 'split' && (
                <>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#374151' }}>
                      Título (headline)
                    </label>
                    <Input
                      value={hostedPage.headline ?? ''}
                      onChange={(e) => updateHostedPage({ headline: e.target.value || undefined })}
                      maxLength={120}
                      data-testid="hosted-page-headline"
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs" style={{ color: '#6b7280' }}>
                      Máx 120 chars. Aparece no painel esquerdo.
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium" style={{ color: '#374151' }}>
                      Subtítulo
                    </label>
                    <Textarea
                      value={hostedPage.subtitle ?? ''}
                      onChange={(e) => updateHostedPage({ subtitle: e.target.value || undefined })}
                      maxLength={280}
                      rows={3}
                      resize="none"
                      data-testid="hosted-page-subtitle"
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="text-xs font-medium" style={{ color: '#374151' }}>
                  Logo (URL)
                </label>
                <div className="mt-1">
                  <UploadInput
                    value={hostedPage.logoUrl ?? ''}
                    onChange={(url) => updateHostedPage({ logoUrl: url || undefined })}
                    placeholder="https://..."
                    accept="image/*"
                    folder="widget-logos"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium" style={{ color: '#374151' }}>
                  Cor de fundo
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="color"
                    value={hostedPage.backgroundColor ?? brandColor}
                    onChange={(e) => updateHostedPage({ backgroundColor: e.target.value })}
                    className="h-9 w-12 rounded border"
                    style={{ borderColor: '#d1d5db' }}
                    data-testid="hosted-page-bg-picker"
                  />
                  <Input
                    value={hostedPage.backgroundColor ?? ''}
                    onChange={(e) => updateHostedPage({ backgroundColor: e.target.value || undefined })}
                    placeholder={brandColor}
                    className="flex-1 font-mono"
                    data-testid="hosted-page-bg"
                  />
                </div>
                <p className="mt-1 text-xs" style={{ color: '#6b7280' }}>
                  Vazio = usa a cor da marca.
                </p>
              </div>
            </div>
          )}

          {hostedPage.enabled && hostedPage.layout === 'fullbleed' && (
            <div
              className="rounded-lg p-3 text-xs"
              style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', color: '#64748b' }}
            >
              O layout Fullbleed usa o estilo padrão do widget — sem customização adicional.
            </div>
          )}
        </div>
      )}
      {/* Tab 5: Funcionalidades */}
      {activeTab === 'features' && (
        <FeaturesTab
          features={features}
          onChange={setFeatures}
        />
      )}
      </div>
    </Drawer>
  );
};

WidgetSettingsModal.displayName = 'WidgetSettingsModal';

// ---------------------------------------------------------------------------
// FeaturesTab sub-components
// ---------------------------------------------------------------------------

interface FeatureRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  dataTestId: string;
}

function FeatureRow({ label, description, checked, onChange, dataTestId }: FeatureRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 pb-3 border-b border-gray-100 last:border-b-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: '#111827' }}>{label}</div>
        <p className="mt-0.5 text-xs leading-relaxed" style={{ color: '#6b7280' }}>{description}</p>
      </div>
      <div data-testid={dataTestId}>
        <Switch checked={!!checked} onChange={onChange} />
      </div>
    </div>
  );
}

interface FeaturesTabProps {
  features: WidgetFeatures;
  onChange: (features: WidgetFeatures) => void;
}

function FeaturesTab({ features, onChange }: FeaturesTabProps) {
  const update = (patch: Partial<WidgetFeatures>) => {
    onChange({ ...features, ...patch });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-4" style={{ borderColor: '#e5e7eb', backgroundColor: '#ffffff' }}>
        <div>
          <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>
            Funcionalidades do widget
          </div>
          <p className="mt-1 text-xs leading-relaxed" style={{ color: '#64748b' }}>
            Controle quais tipos de envio o visitante pode usar no chat. Aplicam-se tanto ao widget
            embed quanto à página pública hospedada.
          </p>
        </div>

        <FeatureRow
          label="Permitir envio de imagens"
          description="Visitante pode enviar JPG, PNG, WEBP, GIF (até 10 MB)."
          checked={!!features.allowImages}
          onChange={(v) => update({ allowImages: v })}
          dataTestId="feature-allowImages"
        />

        <FeatureRow
          label="Permitir gravação de áudio"
          description="Botão de microfone aparece no input. Suporta gravação direta (até 10 MB)."
          checked={!!features.allowAudio}
          onChange={(v) => update({ allowAudio: v })}
          dataTestId="feature-allowAudio"
        />

        <FeatureRow
          label="Permitir envio de documentos"
          description="PDF, Word, Excel, TXT, CSV (até 20 MB)."
          checked={!!features.allowDocuments}
          onChange={(v) => update({ allowDocuments: v })}
          dataTestId="feature-allowDocuments"
        />

        <FeatureRow
          label="Permitir envio de vídeo"
          description="MP4, WebM e outros formatos de vídeo (até 50 MB)."
          checked={!!features.allowVideo}
          onChange={(v) => update({ allowVideo: v })}
          dataTestId="feature-allowVideo"
        />

        <FeatureRow
          label="Permitir instalação do App (PWA)"
          description="Exibe o botão “Instalar App” no chat. A instalação usa o logo e a cor de fundo da Personalização."
          checked={!!features.allowPwaInstall}
          onChange={(v) => update({ allowPwaInstall: v })}
          dataTestId="feature-allowPwaInstall"
        />

        <FeatureRow
          label="Permitir envio de notificações"
          description="Mostra um convite para o visitante autorizar notificações e o avisa das respostas mesmo com a aba fechada. No iPhone, só funciona com o app instalado."
          checked={!!features.allowPushNotifications}
          onChange={(v) => update({ allowPushNotifications: v })}
          dataTestId="feature-allowPushNotifications"
        />
      </div>
    </div>
  );
}
